import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { hasLoadoutCard } from '../utils/cardFeatureDetection';

/**
 * DeathManager Component
 * Handles the death move mechanics when a character marks their last Hit Point.
 * Implements Blaze of Glory, Avoid Death, and Risk It All options with scar system.
 * Also handles the Unbreakable (VALOR L10) domain card special death prevention.
 *
 * @param {object} props - The component props.
 * @param {object} props.character - The current character data object.
 * @param {function} props.updateCharacter - Function to update the character state.
 * @param {function} props.onManagerClose - Callback to close the modal.
 */
const DeathManager = ({ character, updateCharacter, onManagerClose }) => {
  const [selectedMove, setSelectedMove] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [moveResult, setMoveResult] = useState(null);
  const [dualityRoll, setDualityRoll] = useState(null);
  const [scarDescription, setScarDescription] = useState('');
  const [hpToClear, setHpToClear] = useState(0);
  const [stressToClear, setStressToClear] = useState(0);

  // Check if character has Unbreakable equipped and hasn't used it
  const hasUnbreakable = useMemo(() => {
    return hasLoadoutCard(character, 'unbreakable');
  }, [character]);

  const unbreakableUsed = character?.permanentCardChoices?.unbreakable?.chosen === true;
  const canUseUnbreakable = hasUnbreakable && !unbreakableUsed;

  // Build death moves list - include Unbreakable if available
  const deathMoves = [
    // Unbreakable option (only shown if equipped and not used)
    ...(canUseUnbreakable ? [{
      id: 'unbreakable',
      name: 'Unbreakable (Domain Card)',
      icon: '💪',
      description: 'Use your Unbreakable domain card to cheat death',
      details: 'Roll 1d6 and clear that many Hit Points. This card then moves to your vault permanently.',
      color: 'bg-green-600 hover:bg-green-700',
      isSpecial: true
    }] : []),
    {
      id: 'blaze_of_glory',
      name: 'Blaze of Glory',
      icon: '⚡',
      description: 'Embrace death and go out in a blaze of glory',
      details: 'Take one action (at GM\'s discretion) which critically succeeds, then cross through the veil of death.',
      color: 'bg-red-600 hover:bg-red-700'
    },
    {
      id: 'avoid_death',
      name: 'Avoid Death',
      icon: '🛡️',
      description: 'Avoid death and face the consequences',
      details: 'Drop unconscious, can\'t move or act, can\'t be targeted. Return to consciousness when ally clears 1+ HP or after long rest. May gain a scar.',
      color: 'bg-yellow-600 hover:bg-yellow-700'
    },
    {
      id: 'risk_it_all',
      name: 'Risk It All',
      icon: '🎲',
      description: 'Roll your Duality Dice for all or nothing',
      details: 'Hope Die higher: stay up and clear HP/Stress equal to Hope Die value. Fear Die higher: cross through the veil. Critical success: clear all HP and Stress.',
      color: 'bg-purple-600 hover:bg-purple-700'
    }
  ];

  const rollDualityDice = () => {
    const hopeDie = Math.ceil(Math.random() * 12);
    const fearDie = Math.ceil(Math.random() * 12);
    const isCritical = hopeDie === fearDie; // Critical success when both dice roll the same number
    
    return {
      hopeDie,
      fearDie,
      isCritical,
      hopeWins: hopeDie > fearDie,
      fearWins: fearDie > hopeDie,
      tie: hopeDie === fearDie
    };
  };

  const rollHopeDie = () => {
    return Math.ceil(Math.random() * 12);
  };

  const rollD6 = () => {
    return Math.ceil(Math.random() * 6);
  };

  const executeDeathMove = () => {
    setIsProcessing(true);

    setTimeout(() => {
      let result = { moveType: selectedMove };

      switch (selectedMove) {
        case 'unbreakable':
          const d6Roll = rollD6();
          result = {
            ...result,
            outcome: 'unbreakable_success',
            d6Roll,
            message: `Unbreakable! You rolled a ${d6Roll} on your d6. Clear ${d6Roll} Hit Points.`,
            actionNeeded: 'Your indomitable will keeps you fighting. This card now moves to your vault permanently.'
          };
          break;

        case 'blaze_of_glory':
          result = {
            ...result,
            outcome: 'death',
            message: 'Your character embraces death in a blaze of glory. Their final action critically succeeds, leaving a lasting impact before crossing through the veil of death.',
            actionNeeded: 'Work with your GM to describe your final glorious action.'
          };
          break;

        case 'avoid_death':
          const hopeRoll = rollHopeDie();
          const gainsScar = hopeRoll <= character.level;
          
          result = {
            ...result,
            outcome: 'avoided_death',
            hopeRoll,
            gainsScar,
            message: `Your character avoids death. Hope Die roll: ${hopeRoll} ${gainsScar ? `(≤ level ${character.level}, gains a scar)` : `(> level ${character.level}, no scar)`}`,
            actionNeeded: 'Work with your GM to describe how the situation worsens. You temporarily drop unconscious but return to consciousness when an ally clears 1+ HP or after a long rest.'
          };
          break;

        case 'risk_it_all':
          const duality = rollDualityDice();
          setDualityRoll(duality);
          
          if (duality.isCritical) {
            result = {
              ...result,
              outcome: 'critical_success',
              duality,
              message: `Critical Success! Hope: ${duality.hopeDie}, Fear: ${duality.fearDie}. Your character stays up and clears ALL Hit Points and Stress!`,
              actionNeeded: 'Your character miraculously recovers completely.'
            };
          } else if (duality.hopeWins) {
            result = {
              ...result,
              outcome: 'hope_wins',
              duality,
              message: `Hope wins! Hope: ${duality.hopeDie}, Fear: ${duality.fearDie}. Clear ${duality.hopeDie} points between HP and Stress.`,
              actionNeeded: `Distribute ${duality.hopeDie} points between HP and Stress recovery.`,
              pointsToDistribute: duality.hopeDie
            };
          } else if (duality.fearWins) {
            result = {
              ...result,
              outcome: 'death',
              duality,
              message: `Fear wins. Hope: ${duality.hopeDie}, Fear: ${duality.fearDie}. Your character crosses through the veil of death.`,
              actionNeeded: 'Work with your GM to build a new character at the current party level.'
            };
          } else {
            // Tie - treat as Fear wins per rules
            result = {
              ...result,
              outcome: 'death',
              duality,
              message: `Tie result. Hope: ${duality.hopeDie}, Fear: ${duality.fearDie}. Your character crosses through the veil of death.`,
              actionNeeded: 'Work with your GM to build a new character at the current party level.'
            };
          }
          break;
      }

      setMoveResult(result);
      setIsProcessing(false);
    }, 1500);
  };

  const applyResult = () => {
    let updates = { ...character };

    switch (moveResult.outcome) {
      case 'unbreakable_success':
        // Clear HP equal to d6 roll
        updates.hp = Math.min(updates.maxHp, moveResult.d6Roll);
        updates.status = 'active';

        // Move Unbreakable to vault and mark as permanently used
        const currentLoadout = updates.loadout || [];
        const currentVault = updates.vault || [];
        const normalizedCardId = 'core_domain_card_unbreakable';

        updates.loadout = currentLoadout.filter(id => {
          const normalized = id.replace('core_domain_card_', '').replace(/_/g, '-');
          return normalized !== 'unbreakable';
        });
        updates.vault = [...currentVault, normalizedCardId];
        updates.permanentCardChoices = {
          ...(updates.permanentCardChoices || {}),
          unbreakable: {
            chosen: true,
            appliedAt: Date.now(),
            version: 1,
            d6Roll: moveResult.d6Roll,
          },
        };

        toast.success(`💪 Unbreakable! Cleared ${moveResult.d6Roll} HP!`, { duration: 4000 });
        break;

      case 'death':
        // Character dies - mark as deceased
        updates.status = 'deceased';
        updates.hp = 0;
        break;

      case 'avoided_death':
        // Character avoids death, no persistent status
        updates.status = 'active';
        updates.hp = 0;
        
        // Apply scar if needed
        if (moveResult.gainsScar && scarDescription.trim()) {
          const currentScars = updates.scars || [];
          const availableHopeSlots = Math.max(0, 6 - currentScars.length);
          
          if (availableHopeSlots > 0) {
            const nextHopeSlot = 6 - currentScars.length - 1; // Start from rightmost slot (5, 4, 3, etc.)
            updates.scars = [
              ...currentScars,
              {
                id: `scar-${Date.now()}`,
                description: scarDescription.trim(),
                hopeSlot: nextHopeSlot,
                timestamp: new Date().toISOString()
              }
            ];
            
            // Check if all Hope slots are now scarred (journey ends)
            if (updates.scars.length >= 6) {
              updates.status = 'journey_ended';
            }
          }
        }
        break;

      case 'critical_success':
        // Full recovery
        updates.hp = updates.maxHp;
        updates.stress = 0;
        updates.status = 'active';
        break;

      case 'hope_wins':
        // Distribute points between HP and Stress
        const newHp = Math.min(updates.maxHp, Math.max(1, hpToClear));
        const newStress = Math.max(0, updates.stress - stressToClear);
        updates.hp = newHp;
        updates.stress = newStress;
        updates.status = 'active';
        break;
    }

    updateCharacter(updates);
    onManagerClose();
  };

  const currentScars = character.scars?.length || 0;
  const availableHopeSlots = Math.max(0, 6 - currentScars);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-2xl font-bold text-red-600 mb-2">💀 Death Move</h3>
        <p className="text-gray-600 dark:text-gray-400">
          Your character has marked their last Hit Point. Choose your death move.
        </p>
      </div>

      {/* Character Status */}
      <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
        <h4 className="font-semibold text-red-800 dark:text-red-200 mb-2">
          ⚠️ Critical State
        </h4>
        <div className="grid grid-cols-3 gap-4 text-center text-sm">
          <div>
            <div className="font-bold text-red-600">0/{character.maxHp}</div>
            <div className="text-xs">Hit Points</div>
          </div>
          <div>
            <div className="font-bold">Level {character.level}</div>
            <div className="text-xs">Character Level</div>
          </div>
          <div>
            <div className="font-bold text-blue-600">{availableHopeSlots}/6</div>
            <div className="text-xs">Hope Slots</div>
          </div>
        </div>
        {currentScars > 0 && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-2">
            Current Scars: {currentScars}
          </p>
        )}
      </div>

      {!moveResult && (
        <>
          {/* Death Move Selection */}
          <div className="space-y-3">
            <h4 className="font-semibold">Choose Your Death Move</h4>
            {deathMoves.map(move => (
              <button
                key={move.id}
                onClick={() => setSelectedMove(move.id)}
                className={`w-full p-4 rounded-lg text-left transition-all ${
                  selectedMove === move.id 
                    ? `${move.color} text-white` 
                    : 'bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <span className="text-2xl">{move.icon}</span>
                  <div className="flex-1">
                    <div className="font-bold text-lg">{move.name}</div>
                    <div className={`text-sm ${selectedMove === move.id ? 'text-gray-100' : 'text-gray-600 dark:text-gray-400'}`}>
                      {move.description}
                    </div>
                    <div className={`text-xs mt-2 ${selectedMove === move.id ? 'text-gray-200' : 'text-gray-500 dark:text-gray-500'}`}>
                      {move.details}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Execute Button */}
          {selectedMove && (
            <div className="flex space-x-2">
              <button
                onClick={executeDeathMove}
                disabled={isProcessing}
                className="flex-1 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-400 font-medium"
              >
                {isProcessing ? 'Rolling...' : 'Make Death Move'}
              </button>
              <button
                onClick={() => setSelectedMove(null)}
                className="px-6 py-3 bg-gray-500 text-white rounded-md hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          )}
        </>
      )}

      {/* Results */}
      {moveResult && (
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h4 className="font-semibold mb-3">Death Move Result</h4>
            <p className="text-sm mb-3">{moveResult.message}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {moveResult.actionNeeded}
            </p>
          </div>

          {/* Scar Input for Avoid Death */}
          {moveResult.outcome === 'avoided_death' && moveResult.gainsScar && availableHopeSlots > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-200 dark:border-yellow-800">
              <h4 className="font-semibold text-yellow-800 dark:text-yellow-200 mb-3">
                💔 Scar Acquired
              </h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-3">
                You must take a scar. Describe the physical scar, painful memory, or deep fear your character now bears:
              </p>
              <textarea
                value={scarDescription}
                onChange={e => setScarDescription(e.target.value)}
                placeholder="Describe your scar..."
                className="w-full p-3 rounded-md border border-yellow-300 dark:border-yellow-600 bg-white dark:bg-gray-700"
                rows={3}
              />
            </div>
          )}

          {/* Point Distribution for Risk It All Hope Wins */}
          {moveResult.outcome === 'hope_wins' && (
            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
              <h4 className="font-semibold text-green-800 dark:text-green-200 mb-3">
                💚 Distribute Recovery Points
              </h4>
              <p className="text-sm text-green-700 dark:text-green-300 mb-3">
                Distribute {moveResult.pointsToDistribute} points between HP and Stress recovery:
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Clear HP</label>
                  <input
                    type="number"
                    value={hpToClear}
                    onChange={e => {
                      const value = Math.max(0, parseInt(e.target.value) || 0);
                      const remaining = moveResult.pointsToDistribute - value;
                      setHpToClear(Math.min(value, moveResult.pointsToDistribute));
                      setStressToClear(Math.max(0, Math.min(remaining, character.stress)));
                    }}
                    min="0"
                    max={moveResult.pointsToDistribute}
                    className="w-full p-2 rounded border border-green-300 dark:border-green-600 bg-white dark:bg-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Clear Stress</label>
                  <input
                    type="number"
                    value={stressToClear}
                    onChange={e => {
                      const value = Math.max(0, parseInt(e.target.value) || 0);
                      const remaining = moveResult.pointsToDistribute - value;
                      setStressToClear(Math.min(value, Math.min(moveResult.pointsToDistribute, character.stress)));
                      setHpToClear(Math.max(0, remaining));
                    }}
                    min="0"
                    max={Math.min(moveResult.pointsToDistribute, character.stress)}
                    className="w-full p-2 rounded border border-green-300 dark:border-green-600 bg-white dark:bg-gray-700"
                  />
                </div>
              </div>
              <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                Total: {hpToClear + stressToClear}/{moveResult.pointsToDistribute}
              </p>
            </div>
          )}

          {/* Journey End Warning */}
          {moveResult.outcome === 'avoided_death' && moveResult.gainsScar && availableHopeSlots === 0 && (
            <div className="bg-black text-white p-4 rounded-lg border border-gray-600">
              <h4 className="font-semibold mb-2">⚰️ Character's Journey Ends</h4>
              <p className="text-sm">
                Your character has no remaining Hope slots. It's time to end their journey. 
                Work with your GM to find a fitting goodbye at the end of the session.
              </p>
            </div>
          )}

          {/* Apply Result Button */}
          <div className="flex space-x-2">
            <button
              onClick={applyResult}
              disabled={
                (moveResult.outcome === 'avoided_death' && moveResult.gainsScar && availableHopeSlots > 0 && !scarDescription.trim()) ||
                (moveResult.outcome === 'hope_wins' && (hpToClear + stressToClear !== moveResult.pointsToDistribute))
              }
              className="flex-1 py-3 bg-gray-800 text-white rounded-md hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
            >
              Apply Result
            </button>
          </div>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
        <h4 className="font-semibold text-blue-800 dark:text-blue-200 mb-2">
          📚 Death & Resurrection
        </h4>
        <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <p>• Characters who die can potentially be resurrected through difficult quests or the Splendor domain "Resurrection" spell (level 10)</p>
          <p>• Scars are permanent but can be healed through downtime projects or quest rewards at GM's discretion</p>
          <p>• If you lose all Hope slots, it's time for a heroic farewell and a new character</p>
        </div>
      </div>
    </div>
  );
};

export default DeathManager;
